/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { AlertTriangle, FileCheck2, ShieldCheck } from 'lucide-react';
import { chamarApi } from '@/nucleo/auth/cliente-api';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';

const NS = '/bff/v1/j09-s01-registrar-ocorrencia';
const base64 = (arquivo: File) =>
  new Promise<string>((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onerror = () => reject(leitor.error);
    leitor.onload = () => resolve(String(leitor.result).split(',')[1] ?? '');
    leitor.readAsDataURL(arquivo);
  });

export function RegistrarOcorrenciaPage() {
  const [estado, setEstado] = useState<any>();
  const [ocupado, setOcupado] = useState(false);
  const [ordemServicoId, setOrdemServicoId] = useState('');
  const [tipo, setTipo] = useState('DESCUMPRIMENTO_SERVICO');
  const [descricao, setDescricao] = useState('');
  const [arquivo, setArquivo] = useState<File>();

  async function chamar(caminho: string, corpo?: any) {
    setOcupado(true);
    try {
      const envelope = await chamarApi<any>(NS + caminho, {
        metodo: caminho === '/state' ? 'GET' : 'POST',
        corpo,
      });
      setEstado(envelope.state);
      return envelope.state;
    } finally {
      setOcupado(false);
    }
  }

  useEffect(() => {
    void chamar('/state');
  }, []);
  useEffect(() => {
    if (!ordemServicoId && estado?.objetosDisponiveis?.[0])
      setOrdemServicoId(estado.objetosDisponiveis[0].id);
  }, [estado, ordemServicoId]);

  if (!estado)
    return (
      <main role="status" className="p-6">
        Carregando ocorrências…
      </main>
    );
  const ocorrencia = estado.ocorrencias[0];
  return (
    <main className="w-full min-w-0 max-w-full space-y-5 overflow-x-hidden">
      <header>
        <h1 className="text-3xl font-semibold">
          <AlertTriangle className="mr-2 inline h-7 w-7" />
          {estado.titulo}
        </h1>
        <p>{estado.descricao}</p>
        <p className="text-sm text-muted-foreground">
          Fiscal: {estado.sessao?.nome} · {estado.sessao?.email}
        </p>
      </header>
      {estado.alertas.map((a: any, i: number) => (
        <div key={i} role="alert" className="rounded-md border border-destructive p-3">
          {a.mensagem} <span className="font-mono text-xs">{a.codigo}</span>
        </div>
      ))}
      <section className="space-y-3 rounded-md border p-4">
        <h2 className="font-semibold">Fato fiscal</h2>
        <label className="block">
          Recurso ou serviço
          <select
            className="block h-10 w-full rounded-md border bg-background px-3"
            value={ordemServicoId}
            onChange={(e) => setOrdemServicoId(e.target.value)}
          >
            {estado.objetosDisponiveis.map((o: any) => (
              <option key={o.id} value={o.id}>
                OS {o.numero_completo ?? o.numero} · {o.credenciado} · {o.dre}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          Classificação
          <select
            className="block h-10 w-full rounded-md border bg-background px-3"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="DESCUMPRIMENTO_SERVICO">Descumprimento do serviço</option>
            <option value="CONDUTA">Conduta</option>
            <option value="SEGURANCA">Segurança</option>
            <option value="DOCUMENTACAO">Documentação</option>
          </select>
        </label>
        <label className="block" htmlFor="ocorrencia-descricao">
          Narrativa original
          <textarea
            id="ocorrencia-descricao"
            className="mt-1 min-h-28 w-full rounded-md border bg-background p-3"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
          />
        </label>
        <Button
          disabled={ocupado || !ordemServicoId || descricao.trim().length < 20}
          onClick={() =>
            void chamar('/verbs/registrar_ocorrencia', {
              ordemServicoId,
              tipo,
              descricao,
              chaveIdempotencia: crypto.randomUUID(),
            })
          }
        >
          Registrar ocorrência
        </Button>
      </section>
      {ocorrencia && (
        <section className="space-y-3 rounded-md border p-4">
          <h2 className="font-semibold">
            <ShieldCheck className="mr-2 inline h-5 w-5" />
            Cadeia de custódia
          </h2>
          <p>
            <strong>{ocorrencia.protocolo}</strong> · {ocorrencia.tipo} · {ocorrencia.status}
          </p>
          <p>{ocorrencia.descricao}</p>
          <p className="break-all font-mono text-xs">Narrativa {ocorrencia.narrativa_hash}</p>
          <p className="text-sm">
            Autor: {ocorrencia.autor} · Geografia: {ocorrencia.dre} · OS {ocorrencia.origem_numero}
          </p>
          <label className="block" htmlFor="ocorrencia-arquivo">
            Evidência segura
            <Input
              id="ocorrencia-arquivo"
              type="file"
              accept="text/plain,application/pdf,image/png,image/jpeg"
              onChange={(e) => setArquivo(e.target.files?.[0])}
            />
          </label>
          <Button
            disabled={ocupado || !arquivo}
            onClick={async () =>
              arquivo &&
              chamar('/verbs/anexar_evidencia', {
                ocorrenciaId: ocorrencia.id,
                nomeArquivo: arquivo.name,
                tipoMime: arquivo.type || 'text/plain',
                conteudoBase64: await base64(arquivo),
                chaveIdempotencia: crypto.randomUUID(),
              })
            }
          >
            <FileCheck2 className="mr-2 h-4 w-4" />
            Anexar evidência
          </Button>
          <div className="space-y-2">
            {ocorrencia.evidencias.map((e: any) => (
              <article key={e.id} className="rounded-md bg-muted p-3">
                <strong>{e.nome_arquivo}</strong>
                <p>
                  {e.tipo_mime} · {e.tamanho_bytes} bytes
                </p>
                <p className="break-all font-mono text-xs">SHA-256 {e.sha256}</p>
              </article>
            ))}
          </div>
        </section>
      )}
      {estado.resultado && (
        <p role="status" className="rounded-md border border-primary p-3">
          Operação persistida:{' '}
          <span className="font-mono">{estado.resultado.protocolo ?? estado.resultado.sha256}</span>
          {estado.resultado.duplicidadeRelacionada
            ? ' · duplicidade relacionada ao protocolo existente'
            : ''}
        </p>
      )}
    </main>
  );
}
